<template>
  <div class="bg-base-50 p-4 rounded-lg shadow-sm border border-base-200 hover:border-primary transition-colors">
    <div class="text-sm text-base-content/70 font-medium mb-1">
      {{ question.summary?.label }}
    </div>
    <div class="text-base font-medium text-base-content">
      <template v-if="question.type === 'boolean'">
        <span class="badge" :class="badgeClass">{{ label }}</span>
      </template>

      <template v-else-if="question.type === 'list'">
        <ul class="flex flex-wrap gap-2 mt-1">
          <li v-for="(item, idx) in list" :key="idx" class="badge badge-outline">
            {{ item }}
          </li>
        </ul>
      </template>

      <template v-else-if="question.type === 'multiselect'">
        <div v-if="hasSelections">
          <ul class="flex flex-wrap gap-2 mt-1">
            <li v-for="(item, idx) in selections" :key="idx" class="badge badge-primary">
              {{ item }}
            </li>
          </ul>
        </div>
        <div v-else class="text-sm text-base-content/50 italic mt-1">No options selected.</div>
      </template>

      <template v-else-if="['list', 'listinput'].includes(question.type)">
        <ListInput :list="list" @update:list="newList => $emit(newList)" />
      </template>

      <template v-else-if="question.type === 'select'">
        <span class="text-base font-semibold">
          {{ label }}
        </span>
      </template>

      <template v-else-if="question.type === 'long'">
        <blockquote class="border-l-4 border-primary pl-4 italic text-sm text-base-content/90">
          {{ answer }}
        </blockquote>
      </template>

      <template v-else-if="question.type === 'font'">
        <div v-if="label" class="space-y-2 p-4">
          <div class="flex items-center justify-between">
            <p class="text-sm font-normal text-base-content/70">
              Selected Font: <span class="text-base-content font-semibold">{{ answer }}</span>
            </p>
          </div>
          <p :style="{ fontFamily: String(answer) }" class="text-lg text-base-content leading-relaxed tracking-wide">
            The quick brown fox jumps over the lazy dog.
          </p>
        </div>
      </template>

      <template v-else-if="question.type === 'file'">
        <div class="flex items-center gap-4 mt-2">
          <!-- thumbnail container -->
          <div class="w-20 h-20 rounded-lg overflow-hidden border border-base-300">
            <img v-if="answer" :src="answer as string" alt="Uploaded logo preview" class="object-cover w-full h-full" />
            <div v-else class="flex items-center justify-center w-full h-full bg-base-200 text-base-content/50">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7a4 4 0 014-4h10a4 4 0 014 4v11a1 1 0 01-1 1H4a1 1 0 01-1-1V7z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10l2 2 4-4" />
              </svg>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="question.type === 'multifile'">
        <div class="grid grid-cols-3 gap-2 mt-1">
          <img v-for="(img, i) in list" :key="i" :src="img" alt="Uploaded" class="w-full h-auto rounded border" />
        </div>
      </template>

      <template v-else-if="question.type === 'colorpicker'">
        <div class="flex gap-2 mt-1">
          <span
            v-for="color in colors"
            :key="color"
            class="w-6 h-6 rounded-full border border-base-content/10"
            :style="{ backgroundColor: color }"
            :title="color"></span>
        </div>
      </template>

      <template v-else-if="question.type === 'palette'">
        <div v-if="palette" class="space-y-1">
          <p class="text-base-content">{{ palette.label }}</p>
          <div class="flex gap-2 mt-1">
            <span
              v-for="color in palette.colors"
              :key="color"
              class="w-6 h-6 rounded-full border border-base-content/10"
              :style="{ backgroundColor: color }"
              :title="color"></span>
          </div>
        </div>
      </template>

      <template v-else>
        <span class="text-base font-semibold">
          {{ answer }}
        </span>
      </template>
    </div>
    <!-- absolute‐positioned button at top right -->
    <button @click="$emit('fill')" class="absolute top-4 right-4 btn btn-sm btn-outline btn-primary">Edit</button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { QuestionSchema } from "@/features/blueprint/schema/questions/QuestionSchema";
import ListInput from "@/components/forms/outputs/ListInput.vue";

// capture all incoming props in a single object
const props = defineProps<{
  question: QuestionSchema["questions"][number];
  answer: unknown;
  badgeClass: string;
  label: string;
  list: string[];
  colors: string[];
  palette: { label: string; colors: string[] } | null;
}>();

// computed just for multiselect safety
const selections = computed((): string[] => {
  return Array.isArray(props.answer) ? props.answer.filter((v): v is string => typeof v === "string") : [];
});

const hasSelections = computed(() => selections.value.length > 0);

// now refer to props.answer inside your computed
const fileName = computed(() => {
  const ans = props.answer;
  if (!ans) return "";

  // if it’s already a string URL
  if (typeof ans === "string") {
    try {
      return decodeURIComponent(ans.split("/").pop() || "");
    } catch {
      return ans;
    }
  }
  // if it’s a File object
  if ((ans as File).name) {
    return (ans as File).name;
  }
  return "";
});

const emit = defineEmits<{
  (e: "fill"): void;
}>();
</script>
